import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/admin`;

const DataExportModal = ({ userIds, users, onClose }) => {
  const { token } = useAuth();
  const [exportFormat, setExportFormat] = useState('json');
  const [includeProfile, setIncludeProfile] = useState(true);
  const [includePosts, setIncludePosts] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);

  const handleExport = async () => {
    setLoading(true);
    try {
      const exportRequest = {
        user_ids: userIds,
        export_format: exportFormat,
        include_profile: includeProfile,
        include_posts: includePosts,
        ...(dateRangeStart && { date_range_start: dateRangeStart }),
        ...(dateRangeEnd && { date_range_end: dateRangeEnd })
      };

      if (exportFormat === 'csv') {
        // For CSV, we get a file download
        const response = await axios.post(`${API}/users/export`, exportRequest, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        alert('CSV file downloaded successfully');
        onClose();
      } else {
        // For JSON, we display the data
        const response = await axios.post(`${API}/users/export`, exportRequest, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setExportData(response.data);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export user data');
    } finally {
      setLoading(false);
    }
  };

  const downloadJson = () => {
    if (!exportData) return;

    const dataStr = JSON.stringify(exportData.export_data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `users_export_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const copyToClipboard = () => {
    if (!exportData) return;

    const dataStr = JSON.stringify(exportData.export_data, null, 2);
    navigator.clipboard.writeText(dataStr).then(() => {
      alert('Data copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              📥 Export User Data - {userIds.length} Users Selected
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {/* Export Options */}
          {!exportData && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Configuration</h3>
                
                {/* Format Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="format"
                        value="json"
                        checked={exportFormat === 'json'}
                        onChange={(e) => setExportFormat(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">JSON (viewable, programmable)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="format"
                        value="csv"
                        checked={exportFormat === 'csv'}
                        onChange={(e) => setExportFormat(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">CSV (spreadsheet compatible)</span>
                    </label>
                  </div>
                </div>

                {/* Data Scope */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data to Include</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={includeProfile}
                        onChange={(e) => setIncludeProfile(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Profile Information (name, bio, location, etc.)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={includePosts}
                        onChange={(e) => setIncludePosts(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Post Count & Activity Data</span>
                    </label>
                  </div>
                </div>

                {/* Date Range (Optional) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range Filter (Optional)</label>
                  <div className="flex space-x-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <input
                        type="date"
                        value={dateRangeStart}
                        onChange={(e) => setDateRangeStart(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <input
                        type="date"
                        value={dateRangeEnd}
                        onChange={(e) => setDateRangeEnd(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* GDPR Compliance Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-3 text-lg">ℹ️</span>
                    <div>
                      <h4 className="font-medium text-blue-800 mb-1">GDPR Compliance Notice</h4>
                      <p className="text-sm text-blue-700">
                        This export feature is designed to help comply with GDPR data portability requirements. 
                        Ensure you have proper authorization to export this user data and handle it according to your privacy policy.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selected Users Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Selected Users ({userIds.length})</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {users.map(user => (
                      <div key={user.id} className="flex items-center justify-between text-sm bg-white p-2 rounded">
                        <div>
                          <span className="font-medium">{user.username}</span>
                          <span className="text-gray-500 ml-2 text-xs">{user.email}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' :
                          user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          user.status === 'banned' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Export Results (JSON) */}
          {exportData && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  📊 Export Results - {exportData.exported_count} users
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={downloadJson}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                  >
                    📁 Download JSON
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-96">
                <pre className="text-green-400 text-sm whitespace-pre-wrap">
                  {JSON.stringify(exportData.export_data, null, 2)}
                </pre>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Total Records:</strong> {exportData.exported_count}</p>
                <p><strong>Export Format:</strong> JSON</p>
                <p><strong>Export Date:</strong> {new Date().toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          {!exportData && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                ❌ Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </div>
                ) : (
                  '📥 Export Data'
                )}
              </button>
            </>
          )}

          {exportData && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              ✅ Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExportModal;